import { Component, Prop } from 'vue-property-decorator';

import { SwiperOptions } from '@/utils/swiperUtils';
import { VideoJsPlayerOptions } from 'video.js';
import { getDefaultSwiperOptions } from '@/utils/vueUtils';

import { ImageVideoGallerySlide } from '@/common/interfaces/modules/ImageVideoGallery';

import ButtonClose from '@/components/ButtonClose/ButtonClose.vue';
import CarouselPagination from '@/components/CarouselPagination/CarouselPagination.vue';
import CarouselArrow from '@/components/CarouselArrow/CarouselArrow.vue';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer.vue';
import PhotoInfo from './PhotoInfo/PhotoInfo.vue';

import Plus from '@/assets/icons/plus.svg?inline';
import Minus from '@/assets/icons/minus.svg?inline';
import { ShareViewModel } from '@/components/Share/Share.types';

import { mixins } from 'vue-class-component';
import SwiperMixin from '@/mixins/SwiperMixin';
import { getUrlWithDomain } from '@/utils/imageUtils';

@Component({
  components: {
    ButtonClose,
    CarouselPagination,
    CarouselArrow,
    VideoPlayer,
    Plus,
    Minus,
    PhotoInfo,
  },
})
export default class ImageVideoGallery extends mixins(SwiperMixin) {
  @Prop({ type: Array as () => ImageVideoGallerySlide[] })
  readonly slides!: ImageVideoGallerySlide[];

  @Prop({ type: Object as () => ShareViewModel })
  readonly shareData!: ShareViewModel[];

  showSwipers: boolean = false;
  showOverlay: boolean = false;
  showingPhotoInfo: boolean = false;
  imagesAreLoaded: boolean = false;
  swiperIndexData: number = 0;
  wasGalleryOpened: boolean = false;

  slideImageSrc(slide: ImageVideoGallerySlide, skipVideo: boolean = false) {
    const src = slide.Video && !skipVideo ? slide.Video.Poster : slide.Image.Url;

    return getUrlWithDomain(src) + '?height=2000&scale=none&quality=75';
  }

  get lightboxSwiper() {
    return this.getSwiperByRef('lightboxSwiper');
  }

  get thumbnailsSwiper() {
    return this.getSwiperByRef('thumbnailsSwiper');
  }

  get swiperCurrentIndex() {
    return this.lightboxSwiper ? this.lightboxSwiper.activeIndex : 0;
  }

  get currentSlide() {
    return this.slides && this.slides.length > 0 ? this.slides[this.swiperIndexData] : null;
  }

  options: VideoJsPlayerOptions = {
    autoplay: false,
    loop: false,
    muted: false,
    controls: true,
  };

  get swiperMainOptions(): SwiperOptions {
    return {
      ...getDefaultSwiperOptions(this.uuid),
      slidesPerView: 1,
      spaceBetween: 10,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      lazy: {
        loadPrevNext: true,
      },
      navigation: {
        prevEl: `#image-video-gallery__lightbox-swiper-button-prev-${this.uuid}`,
        nextEl: `#image-video-gallery__lightbox-swiper-button-next-${this.uuid}`,
      },
      breakpoints: {
        780: {
          slidesPerView: 1,
        },
      },
    };
  }

  get swiperThumbsOptions(): SwiperOptions {
    return {
      ...getDefaultSwiperOptions(this.uuid),
      lazy: {
        loadPrevNext: true,
      },
      slidesPerView: 'auto',
      spaceBetween: 10,
      touchRatio: 0.5,
      slideToClickedSlide: true,
      centeredSlides: true,
      noSwipingClass: 'swiper-no-swiping',
      navigation: {
        prevEl: `#image-video-gallery__thumbnails-swiper-button-prev-${this.uuid}`,
        nextEl: `#image-video-gallery__thumbnails-swiper-button-next-${this.uuid}`,
      },
      breakpoints: {
        780: {
          slidesPerView: 'auto',
        },
      },
      pagination: {
        el: null,
      },
      on: {
        slideChangeTransitionEnd: () => {
          this.onSlideIndexChange();
        },
      },
    };
  }

  loadGalleryImages() {
    if (this.slides && this.slides.length > 0) {
      const imagesToLoad: Promise<string>[] = [];

      for (let index = 0; index < this.slides.length; index++) {
        imagesToLoad.push(
          new Promise((resolve, reject) => {
            let img = new Image();
            img.onload = () => {
              resolve(img.src);
            };
            img.onerror = (err) => {
              //console.log('error when loading image', img.src);
              resolve(img.src);
            };

            img.src = this.slideImageSrc(this.slides[index]);
          }),
        );
      }

      Promise.all(imagesToLoad)
        .then(() => {
          this.imagesAreLoaded = true;
        })
        .then(() => {
          // this.lightboxSwiper = this.$refs.lightboxSwiper?.swiper;
          // this.thumbnailsSwiper = this.$refs.thumbnailsSwiper?.swiper;

          //two-way controls for swipers
          this.refSwipersControls();
          this.setSwiperElementWidth();
        })
        .catch(() => {
          return false;
        });
    }
  }

  refSwipersControls() {
    if (this.lightboxSwiper && this.thumbnailsSwiper) {
      this.lightboxSwiper.controller.control = this.thumbnailsSwiper;
      // Removed reciprocal control because it was intefering with drag control on thumbbnails swiper
      // this.thumbnailsSwiper.controller.control = this.lightboxSwiper;
    }
  }

  setSwiperElementWidth() {
    // SETTING THE THUMBNAIL 'swiper-slide' element's WIDTH to that of the img it contains, for IE compatibility
    let thumbs = this.$refs.thumbSlides as any;
    for (let i = 0; i < thumbs.length; i++) {
      let thumb = <HTMLElement>thumbs[i].$el;
      const thumbImage = thumb?.querySelector('#thumb_image_' + i);
      if (thumbImage)
        thumb.style.width =
          thumb.querySelector('#thumb_image_' + i).getBoundingClientRect().width + 'px';
    }
    this.thumbnailsSwiper.update();
  }

  onSlideIndexChange() {
    this.resetVideo();
    this.swiperIndexData = this.thumbnailsSwiper?.activeIndex;
    this.lightboxSwiper.slideTo(this.swiperIndexData);
  }

  resetVideo() {
    const videoSlide = this.$refs['video-slide-' + this.swiperIndexData];
    if (videoSlide && videoSlide[0]) {
      videoSlide[0].resetPlayer();
    }
  }

  doOverlayToggle() {
    if (!this.wasGalleryOpened) this.loadGalleryImages();
    this.wasGalleryOpened = true
    if (!this.showOverlay) {
      this.$nextTick(() => {
        this.showOverlay = true;
        document.documentElement.classList.add('no-scroll');
        document.body.classList.add('image-video-gallery-active');
        setTimeout(() => {
          this.showSwipers = true;
        }, 300);
      });
    } else {
      setTimeout(() => {
        this.showOverlay = false;
        document.documentElement.classList.remove('no-scroll');
        document.body.classList.remove('image-video-gallery-active');
      }, 300);
      this.showSwipers = false;
      setTimeout(() => {
        this.showingPhotoInfo = false;
        this.resetVideo();
      }, 1000);
    }
  }

  togglePhotoInfo() {
    this.showingPhotoInfo = !this.showingPhotoInfo;
  }
}
